import s from './SkillFilter.module.css';

export interface SkillFilterOption<K extends string> {
  value: K;
  label: string;
}

interface SkillFilterProps<K extends string> {
  /** Section-kind chips to render, in display order. */
  options: readonly SkillFilterOption<K>[];
  /** Currently active kinds. Empty ⇒ no filter, every row matches. */
  selected: ReadonlySet<K>;
  onToggle: (value: K) => void;
  /** Accessible name for the chip group (e.g. "Filter by skill"). */
  label: string;
}

/**
 * Multi-select toggle chips for narrowing a test list by skill (Listening /
 * Reading / Writing / Speaking). Each chip toggles independently — selecting
 * none means "show every skill", not "show nothing", so callers should treat
 * an empty `selected` set as no filter.
 */
export function SkillFilter<K extends string>({
  options,
  selected,
  onToggle,
  label,
}: SkillFilterProps<K>): JSX.Element {
  return (
    <div className={s.root} role="group" aria-label={label}>
      {options.map((opt) => {
        const active = selected.has(opt.value);
        return (
          <button
            key={opt.value}
            type="button"
            className={`${s.chip} ${active ? (s.chipActive ?? '') : ''}`.trim()}
            aria-pressed={active}
            onClick={() => onToggle(opt.value)}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}
